import { useEffect, useState } from 'react';

import { captureException, setUser } from '@sentry/react';

import { API, auth } from ':api';
import { endpoints } from ':constants';
import { analyticsSetUser } from ':utils/analytics';
import { generateHash } from ':utils/crypto';

export type AuthCheckType = {
  success: boolean | null;
  error: string | null | false;
};

const localStorageAuthErrorKey = 'lke-auth-error';

export function useAuthCheck(): AuthCheckType {
  const [state, setState] = useState<AuthCheckType>({
    success: null,
    error: null,
  });

  (window as any).auth1 = auth;

  useEffect(() => {
    const handleCheck = async (): Promise<void> => {
      try {
        let claims = auth.getClaims();
        const hasNecessaryParams = auth.hasNecessaryParams();
        const hasErrorParam = auth.hasErrorParam();
        const reloadAfterError = localStorage.getItem(localStorageAuthErrorKey);

        if (reloadAfterError) {
          localStorage.removeItem(localStorageAuthErrorKey);
          auth.proceedError();
          return;
        }

        if (hasErrorParam) {
          localStorage.setItem(localStorageAuthErrorKey, '1');
          setState({
            success: false,
            error: 'auth error',
          });
          return;
        }

        if (!claims && !hasNecessaryParams) {
          setState({
            success: false,
            error: false,
          });
          return;
        }

        if (hasNecessaryParams) {
          // if (new Date() > new Date((claims?.exp ?? 0) * 1000)) {
          //   auth.startAuth({ urlParams: { max_age: '0' } });
          //   await new Promise(() => {
          //     //
          //   });
          // }
          await auth.exchangeCode();
        }
        await API.get(endpoints.jwtValidate);
        claims = auth.getClaims();
        if (claims) {
          const employeeNumber = claims['Employee-Number'];
          const groupClaim = claims['group'];
          if (!employeeNumber || !Array.isArray(groupClaim)) {
            auth.startAuth({ urlParams: { max_age: '0' } });
            await new Promise(() => {
              //
            });
          }
          setUser({
            id: employeeNumber,
            email: claims.email,
          });
          const saltedEN = `${employeeNumber}grain_of_salt`;
          generateHash(saltedEN)
            .then(uidHash => {
              analyticsSetUser(uidHash, employeeNumber.startsWith('В'));
            })
            .catch(err => captureException(err));
        }
        setState(prev => ({ ...prev, success: true }));
      } catch (err: unknown) {
        localStorage.setItem(localStorageAuthErrorKey, '1');
        captureException(err);
        setState({
          success: false,
          error: err instanceof Error ? err.message : 'unknown auth error',
        });
      }
    };

    handleCheck();
  }, []);

  return state;
}
